import {
  CheckCircleIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/api';

const EmailTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    description: '',
    category: 'notification',
    isActive: true,
    variables: []
  });
  const [testVariables, setTestVariables] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [emailMode, setEmailMode] = useState('template'); // 'template' or 'custom'
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [customEmailData, setCustomEmailData] = useState({
    subject: '',
    htmlBody: '',
    textBody: ''
  });
  const [templateVariables, setTemplateVariables] = useState({});
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendError, setSendError] = useState('');

  const fetchTemplates = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError('');
      const filters = {};
      if (categoryFilter) filters.category = categoryFilter;
      const response = await adminService.getAllEmailTemplates(filters);
      const fetchedTemplates = response.data.templates || [];
      setTemplates(fetchedTemplates);

      // Update selected template in send modal if it still exists
      if (selectedTemplateName && !fetchedTemplates.find(t => t.name === selectedTemplateName)) {
        setSelectedTemplateName('');
        setTemplateVariables({});
      } else if (selectedTemplateName) {
        // Refresh template data if it was updated
        const updatedTemplate = fetchedTemplates.find(t => t.name === selectedTemplateName);
        if (updatedTemplate && updatedTemplate.variables) {
          const vars = {};
          updatedTemplate.variables.forEach(v => {
            vars[v.name] = templateVariables[v.name] || '';
          });
          setTemplateVariables(vars);
        }
      }
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
      setError(error.response?.data?.message || 'Failed to load email templates');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [categoryFilter]);

  const fetchUsers = async (search = '') => {
    try {
      const response = await adminService.getAllUsers({
        search,
        limit: 50,
        page: 1
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    if (showSendModal && userSearch) {
      const debounceTimer = setTimeout(() => {
        fetchUsers(userSearch);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else if (showSendModal && !userSearch) {
      fetchUsers();
    }
  }, [userSearch, showSendModal]);

  const handleOpenSendModal = () => {
    setShowSendModal(true);
    setSelectedUsers([]);
    setSelectAll(false);
    setUserSearch('');
    setEmailMode('template');
    setSelectedTemplateName('');
    setTemplateVariables({});
    setCustomEmailData({ subject: '', htmlBody: '', textBody: '' });
    setSendError('');
    fetchUsers();
  };

  const handleToggleUser = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === user._id);
      if (isSelected) {
        return prev.filter(u => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
      setSelectAll(false);
    } else {
      const allUsers = users.filter(u => u.role !== 'admin');
      setSelectedUsers([...allUsers]);
      setSelectAll(true);
    }
  };

  const handleSendEmail = async () => {
    if (selectedUsers.length === 0 && !selectAll) {
      setSendError('Please select at least one user or select all users');
      return;
    }

    if (emailMode === 'template' && !selectedTemplateName) {
      setSendError('Please select a template');
      return;
    }

    if (emailMode === 'custom' && (!customEmailData.subject || !customEmailData.htmlBody)) {
      setSendError('Subject and HTML body are required');
      return;
    }

    setSendingEmail(true);
    setSendError('');
    setSuccess('');

    try {
      const emailData = {};

      if (selectAll) {
        emailData.sendToAll = true;
      } else if (selectedUsers.length > 0) {
        emailData.userIds = selectedUsers.map(u => u._id);
      }

      if (emailMode === 'template') {
        emailData.templateName = selectedTemplateName;
        if (Object.keys(templateVariables).length > 0) {
          emailData.variables = templateVariables;
        }
      } else {
        emailData.subject = customEmailData.subject;
        emailData.htmlBody = customEmailData.htmlBody;
        emailData.textBody = customEmailData.textBody;
      }

      const response = await adminService.sendCustomEmail(emailData);
      const recipientCount = response.data?.enqueued || selectedUsers.length || 'all';
      setSuccess(`${recipientCount} email(s) queued successfully`);
      setTimeout(() => {
        setShowSendModal(false);
        setSuccess('');
        setSelectedUsers([]);
        setSelectAll(false);
        setSendError('');
      }, 3000);
    } catch (error) {
      console.error('Failed to send email:', error);
      setSendError(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleTemplateSelect = (templateName) => {
    setSelectedTemplateName(templateName);
    const template = templates.find(t => t.name === templateName);
    if (template && template.variables) {
      const vars = {};
      template.variables.forEach(v => {
        vars[v.name] = '';
      });
      setTemplateVariables(vars);
    } else {
      setTemplateVariables({});
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      htmlBody: '',
      textBody: '',
      description: '',
      category: 'notification',
      isActive: true,
      variables: []
    });
    setTestVariables({});
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody || '',
      description: template.description || '',
      category: template.category,
      isActive: template.isActive,
      variables: template.variables || []
    });
    setTestVariables({});
    setShowModal(true);
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Are you sure you want to delete the template "${name}"?`)) {
      return;
    }

    // Optimistic update - remove from UI immediately
    const deletedTemplate = templates.find(t => t.name === name);
    setTemplates(prev => prev.filter(t => t.name !== name));

    // Clear selection if deleted template was selected in send modal
    if (selectedTemplateName === name) {
      setSelectedTemplateName('');
      setTemplateVariables({});
    }

    try {
      await adminService.deleteEmailTemplate(name);
      setSuccess('Template deleted successfully');
      // Refresh to ensure consistency (without showing loading spinner)
      await fetchTemplates(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to delete template:', error);
      // Revert optimistic update on error
      if (deletedTemplate) {
        setTemplates(prev => [...prev, deletedTemplate].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setError(error.response?.data?.message || 'Failed to delete template');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handlePreview = async (template) => {
    try {
      setLoading(true);
      const response = await adminService.previewEmailTemplate(template.name, testVariables);
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Failed to preview template:', error);
      setError(error.response?.data?.message || 'Failed to preview template');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let updatedTemplate;
      if (editingTemplate) {
        const response = await adminService.updateEmailTemplate(editingTemplate.name, formData);
        updatedTemplate = response.data.template;
        setSuccess('Template updated successfully');

        // Optimistic update - update in UI immediately
        setTemplates(prev => prev.map(t =>
          t.name === editingTemplate.name ? updatedTemplate : t
        ));

        // Update selected template in send modal if it was the one being edited
        if (selectedTemplateName === editingTemplate.name) {
          setSelectedTemplateName(updatedTemplate.name);
          // Update variables if template structure changed
          if (updatedTemplate.variables) {
            const vars = {};
            updatedTemplate.variables.forEach(v => {
              vars[v.name] = templateVariables[v.name] || '';
            });
            setTemplateVariables(vars);
          }
        }
      } else {
        const response = await adminService.createEmailTemplate(formData);
        updatedTemplate = response.data.template;
        setSuccess('Template created successfully');

        // Optimistic update - add to UI immediately
        setTemplates(prev => [...prev, updatedTemplate].sort((a, b) => a.name.localeCompare(b.name)));
      }

      setShowModal(false);
      // Refresh to ensure consistency with server (without showing loading spinner)
      await fetchTemplates(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to save template:', error);
      setError(error.response?.data?.message || 'Failed to save template');
      // Refresh on error to ensure UI matches server state (without showing loading spinner)
      await fetchTemplates(false);
    } finally {
      setSubmitting(false);
    }
  };

  const addVariable = () => {
    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, { name: '', description: '', example: '' }]
    }));
  };

  const updateVariable = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.map((v, i) =>
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const removeVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const categories = ['auth', 'order', 'notification', 'marketing'];

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 border-t-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <XMarkIcon className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-[#008000] px-4 py-3 rounded-lg flex items-center">
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
            Email Template Management
          </h2>
          <p className="text-gray-600 mt-2">Manage email templates for automated communications</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleOpenSendModal}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-shadow flex items-center font-medium"
          >
            <PaperAirplaneIcon className="h-5 w-5 mr-2" />
            Send Email
          </button>
          <button
            onClick={handleCreate}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-shadow flex items-center font-medium"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Template
          </button>
        </div>
      </div>

      <div className="flex gap-4 items-center">
        <label className="text-sm font-medium text-gray-700">Filter by Category:</label>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div
            key={template._id}
            className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-shadow p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg mr-3">
                  <EnvelopeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{template.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${template.category === 'auth' ? 'bg-blue-100 text-blue-800' :
                    template.category === 'order' ? 'bg-green-100 text-[#008000]' :
                      template.category === 'marketing' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                    }`}>
                    {template.category}
                  </span>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${template.isActive ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            </div>

            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description || 'No description'}</p>

            <div className="text-xs text-gray-500 mb-4">
              <p><strong>Subject:</strong> {template.subject.substring(0, 50)}{template.subject.length > 50 ? '...' : ''}</p>
              <p><strong>Variables:</strong> {template.variables?.length || 0}</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(template)}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDelete(template.name)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && !loading && (
        <div className="text-center py-12">
          <EnvelopeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No email templates found</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">
                {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value.toLowerCase() }))}
                    required
                    disabled={!!editingTemplate}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., welcome, order-confirmation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Email subject (use {'{{variableName}}'} for variables)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Template description"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    HTML Body *
                  </label>
                  <span className="text-xs text-gray-500">Use {'{{variableName}}'} for variables</span>
                </div>
                <textarea
                  value={formData.htmlBody}
                  onChange={(e) => setFormData(prev => ({ ...prev, htmlBody: e.target.value }))}
                  required
                  rows={12}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="HTML email template"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Body (Optional)
                </label>
                <textarea
                  value={formData.textBody}
                  onChange={(e) => setFormData(prev => ({ ...prev, textBody: e.target.value }))}
                  rows={6}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Plain text version (optional)"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Variables
                  </label>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="text-sm text-[#008000] hover:text-[#008000] font-medium"
                  >
                    + Add Variable
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.variables.map((variable, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={variable.name}
                        onChange={(e) => updateVariable(index, 'name', e.target.value)}
                        placeholder="Variable name"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="text"
                        value={variable.description || ''}
                        onChange={(e) => updateVariable(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="text"
                        value={variable.example || ''}
                        onChange={(e) => updateVariable(index, 'example', e.target.value)}
                        placeholder="Example"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeVariable(index)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Template is active
                </label>
              </div>

              <div className="flex gap-4 pt-4 border-t">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-shadow font-medium disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1"><strong>Subject:</strong></p>
                <p className="text-lg font-medium">{previewData.subject}</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div dangerouslySetInnerHTML={{ __html: previewData.html }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Send Email</h3>
              <button
                onClick={() => setShowSendModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Users *
                  </label>
                  {users.length > 0 && (
                    <label className="flex items-center text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="mr-2"
                      />
                      Select All ({users.length} users)
                    </label>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users by name or email..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {users.length > 0 && (
                  <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    {users.map((user) => {
                      const isSelected = selectedUsers.some(u => u._id === user._id) || (selectAll && user.role !== 'admin');
                      return (
                        <div
                          key={user._id}
                          onClick={() => handleToggleUser(user)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 flex items-center ${isSelected ? 'bg-blue-50' : ''
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleUser(user)}
                            onClick={(e) => e.stopPropagation()}
                            className="mr-3"
                          />
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{user.name || 'User'}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedUsers.length > 0 && !selectAll && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-[#008000]">
                      {selectedUsers.length} user(s) selected
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedUsers.map((user) => (
                        <span
                          key={user._id}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-[#008000] text-xs"
                        >
                          {user.name || user.email}
                          <button
                            onClick={() => handleToggleUser(user)}
                            className="ml-1 text-[#008000] hover:text-[#008000]"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selectAll && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">
                      All {users.length} users selected
                    </p>
                  </div>
                )}
              </div>

              {/* Email Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="template"
                      checked={emailMode === 'template'}
                      onChange={(e) => setEmailMode(e.target.value)}
                      className="mr-2"
                    />
                    Use Template
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="custom"
                      checked={emailMode === 'custom'}
                      onChange={(e) => setEmailMode(e.target.value)}
                      className="mr-2"
                    />
                    Custom Message
                  </label>
                </div>
              </div>

              {/* Template Selection */}
              {emailMode === 'template' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Template *
                  </label>
                  <select
                    value={selectedTemplateName}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    key={templates.length} // Force re-render when templates change
                  >
                    <option value="">-- Select a template --</option>
                    {templates.filter(t => t.isActive).map(template => (
                      <option key={template._id || template.name} value={template.name}>
                        {template.name} - {template.description || 'No description'}
                      </option>
                    ))}
                  </select>

                  {/* Template Variables */}
                  {selectedTemplateName && Object.keys(templateVariables).length > 0 && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Variables
                      </label>
                      <div className="space-y-2">
                        {Object.keys(templateVariables).map(key => {
                          const template = templates.find(t => t.name === selectedTemplateName);
                          const varInfo = template?.variables?.find(v => v.name === key);
                          return (
                            <div key={key}>
                              <label className="block text-xs text-gray-600 mb-1">
                                {key} {varInfo?.description && `(${varInfo.description})`}
                              </label>
                              <input
                                type="text"
                                value={templateVariables[key] || ''}
                                onChange={(e) => setTemplateVariables(prev => ({
                                  ...prev,
                                  [key]: e.target.value
                                }))}
                                placeholder={varInfo?.example || `Enter ${key}`}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Custom Email Fields */}
              {emailMode === 'custom' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={customEmailData.subject}
                      onChange={(e) => setCustomEmailData(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Email subject"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML Body *
                    </label>
                    <textarea
                      value={customEmailData.htmlBody}
                      onChange={(e) => setCustomEmailData(prev => ({ ...prev, htmlBody: e.target.value }))}
                      rows={10}
                      placeholder="HTML email content (use {'{{variableName}}'} for variables)"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text Body (Optional)
                    </label>
                    <textarea
                      value={customEmailData.textBody}
                      onChange={(e) => setCustomEmailData(prev => ({ ...prev, textBody: e.target.value }))}
                      rows={6}
                      placeholder="Plain text version (optional)"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t">
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || (selectedUsers.length === 0 && !selectAll)}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-shadow font-medium disabled:opacity-50 flex items-center justify-center"
                >
                  {sendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                      Send Email
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowSendModal(false);
                    setSendError('');
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>

              {sendError && (
                <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                  <XMarkIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{sendError}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateManagement;

